// cryptoManager.ts
// Usage: import * as CryptoManager from './cryptoManager'

const CURVE = 'P-256'; // matches Android EC 256
const AES_KEY_LENGTH = 256; // bits
const IV_LENGTH = 12; // bytes for AES-GCM

/* ---------- Helpers: ArrayBuffer <-> Base64 ---------- */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/* ---------- Storage helpers (simple) ---------- */
/* You can swap to IndexedDB wrapper or secure storage. For demo we use localStorage. */
const STORAGE_PUBLIC = 'crypto_pub_spki_base64';
const STORAGE_PRIVATE = 'crypto_priv_pkcs8_base64';

async function storeKeyPairBase64(pubSpkiBase64: string, privPkcs8Base64: string) {
  // Replace with IndexedDB if you prefer
  localStorage.setItem(STORAGE_PUBLIC, pubSpkiBase64);
  localStorage.setItem(STORAGE_PRIVATE, privPkcs8Base64);
}

export async function getStoredPublicKeyBase64(): Promise<string | null> {
  return localStorage.getItem(STORAGE_PUBLIC);
}

export async function getStoredPrivateKeyBase64(): Promise<string | null> {
  return localStorage.getItem(STORAGE_PRIVATE);
}

/* ---------- Generate & store keypair ---------- */
export async function generateAndStoreKeyPair(): Promise<{ publicKeyBase64: string; privateKeyBase64: string }> {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'ECDH',
      namedCurve: CURVE
    },
    true, // extractable so we can export pkcs8/spki (if you set to false you cannot export)
    ['deriveKey', 'deriveBits'] // operations used for ECDH
  );

  // export public as spki (X.509), private as pkcs8
  const pubSpki = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  const privPkcs8 = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

  const pubBase64 = arrayBufferToBase64(pubSpki);
  const privBase64 = arrayBufferToBase64(privPkcs8);

  await storeKeyPairBase64(pubBase64, privBase64);
  return { publicKeyBase64: pubBase64, privateKeyBase64: privBase64 };
}

/* ---------- Importers (from your Android base64) ---------- */
export async function importPublicKeyFromBase64(spkiBase64: string): Promise<CryptoKey> {
  const ab = base64ToArrayBuffer(spkiBase64);
  return crypto.subtle.importKey(
    'spki',
    ab,
    {
      name: 'ECDH',
      namedCurve: CURVE
    },
    true,
    []
  );
}

export async function importPrivateKeyFromBase64(pkcs8Base64: string): Promise<CryptoKey> {
  const ab = base64ToArrayBuffer(pkcs8Base64);
  return crypto.subtle.importKey(
    'pkcs8',
    ab,
    {
      name: 'ECDH',
      namedCurve: CURVE
    },
    true,
    ['deriveBits', 'deriveKey']
  );
}

/* ---------- Derive AES key from ECDH shared secret ---------- */
async function deriveAesKey(myPriv: CryptoKey, theirPub: CryptoKey): Promise<CryptoKey> {
  // deriveBits(256) to get 256-bit raw shared secret and then import as AES-GCM key
  // We use deriveBits + importKey rather than deriveKey so we can match deriveAesKey via SHA-256 if needed.
  const sharedBits = await crypto.subtle.deriveBits(
    {
      name: 'ECDH',
      public: theirPub
    },
    myPriv,
    256 // 256 bits (32 bytes) -> AES-256
  );

  // Option 1: Use raw derived bits directly as AES key material (this mirrors deriving SHA-256 over shared secret,
  // because deriveBits for ECDH with P-256 already produces suitable bytes). If you need an explicit SHA-256 hash step
  // to match Android's `MessageDigest.getInstance("SHA-256").digest(shared)`, you can hash sharedBits below.
  // To be identical to Android's flow (hash the raw shared secret), do:
  const sharedBytes = new Uint8Array(sharedBits);
  const hashed = new Uint8Array(await crypto.subtle.digest('SHA-256', sharedBytes.buffer));

  return crypto.subtle.importKey(
    'raw',
    hashed.buffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

/* ---------- AES-GCM encrypt -> base64(iv || ciphertextAndTag) ---------- */
export async function encryptWithECDH(myPrivateBase64: string, recipientPublicBase64: string, plainText: string): Promise<string> {
  const myPriv = await importPrivateKeyFromBase64(myPrivateBase64);
  const theirPub = await importPublicKeyFromBase64(recipientPublicBase64);

  const aesKey = await deriveAesKey(myPriv, theirPub);

  // iv
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const encoded = new TextEncoder().encode(plainText);
  const cipherBuffer = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv,
      tagLength: 128
    },
    aesKey,
    encoded
  );

  // concat iv + cipher
  const out = new Uint8Array(iv.length + cipherBuffer.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(cipherBuffer), iv.length);

  return arrayBufferToBase64(out.buffer);
}

/* ---------- AES-GCM decrypt (base64(iv||ciphertextAndTag)) ---------- */
export async function decryptWithECDH(myPrivateBase64: string, senderPublicBase64: string, base64Payload: string): Promise<string> {
  const myPriv = await importPrivateKeyFromBase64(myPrivateBase64);
  const theirPub = await importPublicKeyFromBase64(senderPublicBase64);

  const aesKey = await deriveAesKey(myPriv, theirPub);

  const all = new Uint8Array(base64ToArrayBuffer(base64Payload));
  const iv = all.slice(0, IV_LENGTH);
  const cipher = all.slice(IV_LENGTH);

  const plainBuffer = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: iv,
      tagLength: 128
    },
    aesKey,
    cipher
  );

  return new TextDecoder().decode(plainBuffer);
}

/* ---------- Convenience: Prepare public key for server registration ---------- */
export async function ensureKeyPairAndGetPublicBase64(): Promise<string> {
  const existing = await getStoredPublicKeyBase64();
  if (existing) return existing;
  const created = await generateAndStoreKeyPair();
  return created.publicKeyBase64;
}

/* Exported functions summary */
export default {
  generateAndStoreKeyPair,
  getStoredPublicKeyBase64,
  getStoredPrivateKeyBase64,
  importPublicKeyFromBase64,
  importPrivateKeyFromBase64,
  encryptWithECDH,
  decryptWithECDH,
  ensureKeyPairAndGetPublicBase64
};
